import { SOFTWARE_WEIGHTS, GAME_WEIGHTS, type OptionWeights } from "./apps-games-catalog";
import { SCALERS } from "./scalers";
import { GAME_TIERS, SOFTWARE_TIERS, HEAVY_UE5_ANCHOR } from "./apps-games-tiers";

type Answer = string | string[] | number | boolean | null;

// Budget at/above which a gaming request targets the 1440p tier instead of 1080p.
// The outreach schema has no resolution/refresh question yet (run 1 §5), so budget is
// the only signal — this threshold is an unvalidated, currency-dependent PROPOSAL
// (recommendation-target-selection-prompt.md §3, §8), kept as one named constant to tune.
const TIER_1440_BUDGET = 1400;

// GPU/CPU targets selected from the tiered game/software catalogs (behind the useTiers
// flag). Replaces the coarse "max of a 9-value column then lift ×1.6" path with a real
// per-title, per-resolution target. Returns null for an axis nothing stresses.
function selectTierTargets(
  games: string[], software: string[], aaaGaming: boolean, budget: number | undefined,
): { gpu: number | null; cpu: number | null } {
  const use1440 = budget !== undefined && budget >= TIER_1440_BUDGET;
  let gpu = 0, cpu = 0, hasGpu = false, hasCpu = false;

  // Games: max of the chosen resolution tier for GPU; CPU always the 60fps column (no
  // high-refresh signal in the schema, and resolution doesn't drive CPU load — §3 step 3).
  for (const g of games) {
    const t = GAME_TIERS[g];
    if (!t) continue;
    const gv = use1440 ? t.ts_1440p_high_60 : t.ts_1080p_high_60;
    if (typeof gv === "number") { gpu = Math.max(gpu, gv); hasGpu = true; }
    if (typeof t.cb_1080p_high_60 === "number") { cpu = Math.max(cpu, t.cb_1080p_high_60); hasCpu = true; }
  }
  // aaa_gaming with no title → the heavy-ue5 class anchor at the chosen tier (§3 step 4),
  // so it scales with resolution instead of being a fixed number.
  if (aaaGaming) {
    const gv = use1440 ? HEAVY_UE5_ANCHOR.ts_1440p_high_60 : HEAVY_UE5_ANCHOR.ts_1080p_high_60;
    gpu = Math.max(gpu, gv); hasGpu = true;
    cpu = Math.max(cpu, HEAVY_UE5_ANCHOR.cb); hasCpu = true;
  }

  // Software (standard tier — no professional-use signal in the schema yet, §4/§8). CPU
  // takes the max across apps. GPU only takes a raise from good/proxy-confidence rows:
  // 178/235 rows are `poor` confidence with a floor marker, and a poor *non-floor* value
  // must not raise the target above what a measured row asks (§4 step 3).
  let swGpuConfident = 0;
  for (const s of software) {
    const t = SOFTWARE_TIERS[s];
    if (!t) continue;
    if (typeof t.cb_standard === "number") { cpu = Math.max(cpu, t.cb_standard); hasCpu = true; }
    if ((t.gpu_axis_confidence === "good" || t.gpu_axis_confidence === "proxy")
        && typeof t.ts_standard === "number") {
      swGpuConfident = Math.max(swGpuConfident, t.ts_standard);
    }
  }
  if (swGpuConfident > 0) { gpu = Math.max(gpu, swGpuConfident); hasGpu = true; }

  return { gpu: hasGpu ? gpu : null, cpu: hasCpu ? cpu : null };
}

// Tier 3.17: how heavily battery efficiency counts, by usage. Previously a flat
// 2.0 for everyone, which meant a desk user who never unplugs weighted battery as
// heavily as a daily commuter — pulling every result toward the same handful of
// efficient ultrabooks regardless of what the person actually needed. Now the
// weight tracks usage: negligible at a desk, moderate for hybrid, full for mobile
// (where battery genuinely decides the purchase). `usage` sets the efficiency
// *target*; this sets its *weight*.
const USAGE_EFFICIENCY_WEIGHT: Record<string, number> = {
  desk: 0.5,
  hybrid: 1.0,
  mobile: 2.0,
};

/* ─────────────────────────────────────────────────────────────
   Weight tables — what each option demands from the hardware.
   SOFTWARE_WEIGHTS / GAME_WEIGHTS are generated from the source
   CSVs in apps-games-catalog.ts; the rest are hand-tuned below.
───────────────────────────────────────────────────────────── */

const USAGE_WEIGHTS: Record<string, OptionWeights> = {
  desk: {
    review_power_efficiency: 20,
    review_energy_efficiency: 20,
  },
  hybrid: {
    review_power_efficiency: 60,
    review_energy_efficiency: 60,
  },
  mobile: {
    review_power_efficiency: 90,
    review_energy_efficiency: 90,
  },
};

// Only OSes we actually stock in volume are selectable. Linux (1 config) and
// No-OS/DOS (~94) are deliberately NOT offered — we can't fulfil them — and Chrome
// OS isn't a recommendation target either. The value maps to a representative
// operating_system string the API resolves to an OS *family* for hard filtering.
const OS_MAP: Record<string, string> = {
  windows: "Windows 11 Pro",
  mac: "macOS",
};

// Software that has no native ARM-Windows build and so must not be answered with a
// Snapdragon/ARM machine (Tier 3.13). Selecting any of these adds "arm" to the
// query's excludeInstructionSets, which the API treats as a hard filter. Kept
// deliberately conservative — apps with real ARM-Windows or Apple-Silicon builds
// (VS Code, the Adobe video apps, most DAWs) are left out so we don't wrongly
// exclude ARM for them. This should eventually become a column in v2/software.csv.
const X86_ONLY_SOFTWARE = new Set<string>([
  "solidworks", "catia", "ansys-workbench", "autodesk-inventor", "autodesk-revit",
  "autocad", "autocad-civil-3d", "autodesk-fusion-360", "fusion-360", "3ds-max",
  "altium-designer", "cadence-virtuoso", "labview", "fpga-vivado", "matlab-simulink",
  "pro-tools", "vmware-workstation", "virtualbox", "blender",
]);

// Lever 2: software whose real-world use wants 32GB RAM (CAD / 3D / pro video / DAW /
// ML / VMs). Selecting any lifts the RAM target to 32. Large-media / project apps also
// lift the storage target to 1TB. Conservative, hand-picked; a hobbyist on a tight
// budget still gets their best-available config (soft target, no hard drop).
const RAM32_SOFTWARE = new Set<string>([
  "solidworks", "catia", "ansys-workbench", "autodesk-inventor", "autodesk-revit",
  "autocad", "autocad-civil-3d", "autodesk-fusion-360", "fusion-360", "3ds-max",
  "cadence-virtuoso", "altium-designer", "matlab-simulink", "fpga-vivado", "labview",
  "vmware-workstation", "virtualbox", "pytorch-tensorflow",
  "adobe-premiere-pro", "adobe-after-effects", "davinci-resolve",
  "pro-tools", "cubase-reaper", "ableton-live", "logic-pro",
  "blender", "unreal-engine", "unity",
]);
const STORAGE1TB_SOFTWARE = new Set<string>([
  "adobe-premiere-pro", "adobe-after-effects", "davinci-resolve", "blender",
  "unreal-engine", "unity", "pro-tools", "cubase-reaper", "ableton-live",
  "vmware-workstation", "virtualbox", "3ds-max",
]);
// GPU-heavy creative software: benefits enough from a dedicated GPU that we prefer one
// (soft, affordability-gated server-side like Tier 5.3). Distinct from RAM32_SOFTWARE
// (memory) and X86_ONLY_SOFTWARE (compatibility).
const GPU_HEAVY_SOFTWARE = new Set<string>([
  "davinci-resolve", "adobe-premiere-pro", "adobe-after-effects", "obs-studio",
  "blender", "unreal-engine", "unity", "3ds-max",
]);

const SCREEN_TO_FORM_FACTOR: Record<string, number> = {
  "13": 1,
  "14": 1,
  "15": 2,
  "16": 2,
};

const ALL_BRANDS = ["apple", "dell", "lenovo", "hp", "asus", "msi", "aorus", "gigabyte", "acer", "alienware"];

// Representative demanding-AAA target (Tier 3.15). When a poster wants AAA gaming
// but names no specific title, the extractor sets `aaa_gaming` and we apply this
// instead of leaving `games` empty — which used to collapse the GPU target to ~700
// and return integrated-graphics ultrabooks to someone who asked for AAA. Values
// mirror the catalog's modern-AAA tier (Battlefield 6 / AC Shadows / Spider-Man 2:
// Time Spy 7534, cinebench 846, 16 GB, 512 GB SSD). Tier 3.11 then lifts it further
// toward what the budget can actually buy.
const AAA_GAMING_WEIGHTS: OptionWeights = {
  cinebench_2024: 846,
  three_d_mark_time_spy_g: 7534,
  review_power_efficiency: 52,
  review_energy_efficiency: 46,
  ram_size_gigabytes: 16,
  storage_size_gigabytes: 512,
  storage_type: "SSD",
};

const HAS_FEATURE_KEYS = [
  "has_hdmi",
  "has_ethernet",
  "has_mini_display_port",
  "has_card_reader",
  "has_finger_print_reader",
  "has_speakers",
] as const;

/* ─────────────────────────────────────────────────────────────
   buildRecommendationQuery
   Returns the features object (max of each array) plus the
   top-level fields: budget, country, brands.
───────────────────────────────────────────────────────────── */

export function buildRecommendationQuery(
  answers: Record<string, Answer>,
  opts?: { useTiers?: boolean },
) {
  // Numeric feature accumulator arrays.
  // Performance is driven by exactly four features now: CPU cinebench_2024 +
  // review_power_efficiency, and GPU three_d_mark_time_spy_g +
  // review_energy_efficiency. applyWeights() only writes keys present here, so
  // no other performance feature can be edited by an option.
  const num: Record<string, number[]> = {
    cinebench_2024: [],
    review_power_efficiency: [],
    three_d_mark_time_spy_g: [],
    review_energy_efficiency: [],
    storage_size_gigabytes: [],
    ram_size_gigabytes: [],
    form_factor: [],
    has_hdmi: [],
    has_ethernet: [],
    has_mini_display_port: [],
    has_card_reader: [],
    has_finger_print_reader: [],
    has_speakers: [],
  };

  // String feature accumulator arrays
  const str: Record<string, string[]> = {
    storage_type: [],
    operating_system: [],
  };

  function applyWeights(weights: OptionWeights) {
    for (const [key, val] of Object.entries(weights)) {
      if (key === "storage_type") {
        if (val) str.storage_type.push(val as string);
      } else if (typeof val === "number" && key in num) {
        num[key].push(val);
      }
    }
  }

  // IDs that matched nothing in the catalog. They are dropped from the query
  // either way, but silently dropping them is dangerous: an ID that is merely
  // misspelled ("premiere-pro" for "adobe-premiere-pro") removes the entire
  // performance requirement and the request still succeeds, returning office
  // laptops to a video editor. Callers should treat a non-empty list as a bug.
  const unknownIds: string[] = [];

  // Software
  for (const sw of (answers["software"] as string[] | undefined) ?? []) {
    const w = SOFTWARE_WEIGHTS[sw];
    if (w) applyWeights(w);
    else unknownIds.push(sw);
  }

  // Games. Track the heaviest GPU demand named, for the Tier 5.3 discrete-GPU hint.
  let maxGameGpu = 0;
  for (const game of (answers["games"] as string[] | undefined) ?? []) {
    const w = GAME_WEIGHTS[game];
    if (w) {
      applyWeights(w);
      if (typeof w.three_d_mark_time_spy_g === "number") {
        maxGameGpu = Math.max(maxGameGpu, w.three_d_mark_time_spy_g);
      }
    } else unknownIds.push(game);
  }

  // "AAA gaming", no specific title named (Tier 3.15) → a representative demanding
  // basket rather than an empty games array.
  if (answers["aaa_gaming"]) {
    applyWeights(AAA_GAMING_WEIGHTS);
    maxGameGpu = Math.max(maxGameGpu, AAA_GAMING_WEIGHTS.three_d_mark_time_spy_g ?? 0);
  }

  // Usage location
  const usage = answers["usage"] as string | undefined;
  if (usage && USAGE_WEIGHTS[usage]) applyWeights(USAGE_WEIGHTS[usage]);

  // OS
  const osKey = answers["os"] as string | undefined;
  if (osKey && OS_MAP[osKey]) str.operating_system.push(OS_MAP[osKey]);

  // Screen size → form_factor
  const screen = answers["screen"] as string | undefined;
  if (screen !== undefined && SCREEN_TO_FORM_FACTOR[screen] !== undefined) {
    num.form_factor.push(SCREEN_TO_FORM_FACTOR[screen]);
  }

  // Has-features: 1 when the user asked for it, nothing otherwise.
  //
  // Not ticking a feature is not a request for its absence. These are exact-match
  // features server-side, so sending 0 scored a laptop 0 for *having* an HDMI port
  // the user simply never mentioned — six features' worth of penalty aimed at the
  // best-connected machines. Pushing nothing leaves the feature null, which drops
  // it from scoring instead.
  const selectedFeatures = (answers["features"] as string[] | undefined) ?? [];
  for (const key of HAS_FEATURE_KEYS) {
    if (selectedFeatures.includes(key)) num[key].push(1);
  }

  // Resolve: max of each numeric array, last value of each string array, null when no data
  const features: Record<string, number | string | null> = {
    // No question asks for a business-grade machine, so we no longer assert one.
    // It was hardcoded to 1 on every request while every config in the catalog
    // stores boolean true, so it contributed a flat penalty to every laptop and
    // discriminated between none of them.
    is_business_grade: null,
    // Features not driven by any question — always null (determined server-side)
    num_of_usb_a: null,
    num_of_usb_c: null,
    resolution: null,
    wifi_version: null,
    booltooth_version: null,
  };

  for (const [key, arr] of Object.entries(num)) {
    features[key] = arr.length > 0 ? Math.max(...arr) : null;
  }
  for (const [key, arr] of Object.entries(str)) {
    features[key] = arr.length > 0 ? arr[arr.length - 1] : null;
  }

  // Lever 2 (partly-to-yes-conversion.md): infer RAM/storage TARGETS from the workload.
  // The catalog weights peak RAM as low as 8, so a 16GB config could outscore a 32GB one
  // on overshoot — the "16GB is thin for CAD/Blender/DAW/4K-editing" complaint. Set a
  // sensible 2026 baseline (16GB / 512GB — nobody should be steered to 8GB) and lift it
  // for heavy workloads (32GB, and 1TB for large-media apps). Soft targets, not hard
  // floors: a low-budget buyer still gets their best-available config, just scored so the
  // most RAM/storage they can afford wins. Pairs with Lever 1's raised weights.
  const swSet = new Set((answers["software"] as string[] | undefined) ?? []);
  const heavyRam = [...swSet].some((s) => RAM32_SOFTWARE.has(s));
  const largeStorage = [...swSet].some((s) => STORAGE1TB_SOFTWARE.has(s));
  const ramTarget = Math.max(Number(features.ram_size_gigabytes) || 0, 16, heavyRam ? 32 : 0);
  const storageTarget = Math.max(Number(features.storage_size_gigabytes) || 0, 512, largeStorage ? 1000 : 0);
  features.ram_size_gigabytes = ramTarget;
  features.storage_size_gigabytes = storageTarget;

  // Top-level fields
  const rawBudget = answers["budget"];
  const budget =
    rawBudget !== null && rawBudget !== undefined
      ? parseFloat(String(rawBudget))
      : undefined;

  const country = (answers["country"] as string | undefined) ?? undefined;

  // Tier-based GPU/CPU targets (behind useTiers flag). Overrides the coarse catalog-max
  // values with a per-title, per-resolution target from the tiered catalogs. The axes it
  // sets go into `reanchorSkip` so the server does NOT also apply the Tier 3.11 ×1.6/×1.25
  // + budget-P80 uplift — the tier value already encodes a good experience, and lifting it
  // again double-counts and clamps everything to the catalog ceiling (§5).
  const reanchorSkip: string[] = [];
  if (opts?.useTiers) {
    const games = (answers["games"] as string[] | undefined) ?? [];
    const software = (answers["software"] as string[] | undefined) ?? [];
    const tier = selectTierTargets(games, software, Boolean(answers["aaa_gaming"]), budget);
    if (tier.gpu !== null) {
      features.three_d_mark_time_spy_g = tier.gpu;
      reanchorSkip.push("three_d_mark_time_spy_g");
    }
    if (tier.cpu !== null) {
      features.cinebench_2024 = tier.cpu;
      reanchorSkip.push("cinebench_2024");
    }
  }

  const brandAnswer = answers["brand"] as string[] | string | undefined;
  const brandArr = Array.isArray(brandAnswer) ? brandAnswer : (brandAnswer && brandAnswer !== "none" ? [brandAnswer] : []);
  const brands = brandArr.length > 0 ? brandArr : ALL_BRANDS;

  // Hard exclusions (Tier 3.13). Instruction set: x86-only software forces out ARM
  // machines; a caller can also pass an explicit list (e.g. a "no ARM" answer).
  // Brands: an explicit "don't want" list (e.g. "no Apple"). Both default to empty
  // and are applied server-side as hard filters, never as soft score penalties.
  const softwareSel = (answers["software"] as string[] | undefined) ?? [];
  const requiresX86 = softwareSel.some((s) => X86_ONLY_SOFTWARE.has(s));
  const explicitExclInstr = (answers["exclude_instruction_sets"] as string[] | undefined) ?? [];
  const excludeInstructionSets = Array.from(
    new Set([...explicitExclInstr, ...(requiresX86 ? ["arm"] : [])]),
  );
  const excludeBrandsAnswer = answers["exclude_brands"] as string[] | string | undefined;
  const excludeBrands = Array.isArray(excludeBrandsAnswer)
    ? excludeBrandsAnswer
    : excludeBrandsAnswer
      ? [excludeBrandsAnswer]
      : [];

  // Dedicated-GPU requirement (Tier 3.14): a hard filter server-side, not a scored
  // feature — "I need a dedicated graphics card" is a yes/no requirement.
  const requireDiscreteGpu = Boolean(answers["discrete_gpu"]);

  // Tier 5.3: a demanding-gaming request (an AAA-tier title, or the AAA basket)
  // auto-prefers a dedicated GPU even when the poster didn't say "dedicated GPU"
  // outright — this is the strong-integrated-APU case where a $2k gamer was handed
  // a Radeon 8050S. Applied SOFTLY: the server drops it when no discrete GPU is in
  // budget, so a low-budget gamer still gets their best integrated option rather
  // than no_coverage. Light titles (Roblox/Minecraft/Sims, GPU target < 5000) stay
  // below the threshold and are untouched.
  const DEMANDING_GAMING_GPU = 5000;
  // Also auto-prefer a dedicated GPU for GPU-heavy creative software (DaVinci, OBS,
  // Premiere/AE, Blender, Unreal) — the "DaVinci/OBS user got an iGPU ultrabook" misses.
  const gpuHeavySoftware = [...swSet].some((s) => GPU_HEAVY_SOFTWARE.has(s));
  // ...but NOT for a portability-first / carried-daily poster: pushing a thin-and-light
  // user onto a 16" gaming brick for a single creative app was a recurring miss (a
  // "light on-the-go editor" handed a gaming laptop). A named demanding GAME still
  // auto-prefers (that user knowingly wants the GPU); only the software auto-imply is
  // suppressed here. An explicit `discrete_gpu` is untouched — it's a stated requirement.
  const portabilityFirst = usage === "mobile" || answers["priority"] === "portability";
  const preferDiscreteGpu = !requireDiscreteGpu
    && (maxGameGpu >= DEMANDING_GAMING_GPU || (gpuHeavySoftware && !portabilityFirst));

  // Minimum-spec floors (Tier 3.16): "16GB minimum", "at least 1TB" are hard floors,
  // applied server-side as exclusions rather than as the soft ram/storage targets.
  const numOrUndef = (v: Answer): number | undefined => {
    const n = typeof v === "number" ? v : v != null ? parseFloat(String(v)) : NaN;
    return Number.isFinite(n) && n > 0 ? n : undefined;
  };
  const minRamGb = numOrUndef(answers["min_ram_gb"]);
  const minStorageGb = numOrUndef(answers["min_storage_gb"]);

  // CPU-tier floor: a stated "not an N-series ewaste chip / at least a real i5"
  // (`modern`) or "i7 or better / high-end CPU" (`strong`) becomes a hard cinebench
  // floor server-side, dropping the low-tier chips it explicitly rules out.
  const MIN_CPU_CINEBENCH: Record<string, number> = { modern: 420, strong: 820 };
  const minCpuCinebench = MIN_CPU_CINEBENCH[String(answers["min_cpu_tier"] ?? "")];

  // Openness to refurbished stock. Like requireDiscreteGpu this is a hard
  // server-side filter rather than a scored feature — it decides which listings
  // exist for a laptop, not how good the laptop is. It only ever *widens* the set,
  // so an unanswered question means new-only, exactly as before the question
  // existed. The quiz answers "yes"/"no", so the string is compared rather than
  // coerced — "no" is truthy.
  const allowRefurbished = answers["refurbished"] === "yes" || answers["refurbished"] === true;

  // A stated touchscreen requirement. Like requireDiscreteGpu this is a hard
  // server-side filter rather than a scored has-feature: a laptop without a touch
  // panel does not partly satisfy "I need a touchscreen", so scoring it lower is the
  // wrong shape. Deliberately NOT in HAS_FEATURE_KEYS for that reason. Unanswered
  // means no filter, so results are unchanged for everyone who never asked.
  //
  // Two entry points: the website quiz ships it inside the "must-have features"
  // checklist (it reads as a feature to a user, even though it is plumbed as a
  // filter), while the outreach extractor sets a standalone yes/no from post text.
  const requireTouchscreen =
    selectedFeatures.includes("is_touchscreen")
    || answers["touchscreen"] === "yes"
    || answers["touchscreen"] === true;

  // Tier 3.17: usage-scaled efficiency weight (see USAGE_EFFICIENCY_WEIGHT). The
  // rest of the scalers are unchanged. Sent with the request so the ranker weights
  // battery according to how the person actually uses the machine.
  const effWeight = USAGE_EFFICIENCY_WEIGHT[usage ?? ""] ?? USAGE_EFFICIENCY_WEIGHT.hybrid;
  const scalers = {
    ...SCALERS,
    review_power_efficiency: effWeight,
    review_energy_efficiency: effWeight,
  };

  // Lever 3 (partly-to-yes-conversion.md): heavily up-weight the ONE dimension the
  // poster ranked as most important, beyond what `usage` alone implies. This targets
  // the UNDER-served priorities the analysis found (battery / portability decide the
  // purchase on 26/36 partly posts). `performance` is deliberately a no-op: CPU/GPU are
  // already the top-weighted features (5), so a "performance" ranking is already served
  // — boosting it further would only fight Levers 1/2's RAM/storage promotion. Battery
  // and portability map onto efficiency (no dedicated weight/thinness feature yet —
  // that's Lever 4; efficiency is the closest proxy for portability).
  switch (answers["priority"]) {
    case "battery":
      scalers.review_power_efficiency = 5;
      scalers.review_energy_efficiency = 5;
      break;
    case "portability":
      scalers.review_power_efficiency = 4;
      scalers.review_energy_efficiency = 4;
      break;
    case "storage":
      scalers.storage_size_gigabytes = 7;
      break;
    case "display":
      scalers.resolution = 4;
      break;
  }

  // Portability re-rank (partly-to-yes): weight is what "portable" actually means, but
  // the engine only had efficiency as a proxy — and a heavy 16" laptop can be efficient,
  // so a 2.2kg machine outranked a 1.2kg one for a weight-first user (the "16in M3 Pro
  // over the MacBook Air" miss). Send weight_kg as a real LOWER_IS_BETTER target: a
  // light-ultrabook target (1.3kg) weighted heavily when weight is the stated #1
  // priority, and a moderate target/weight for a carried-daily (mobile) user who didn't
  // rank it #1. Left unset (inert) for desk/hybrid users who never signalled portability.
  if (answers["priority"] === "portability") {
    features.weight_kg = 1.3;
    scalers.weight_kg = 6;
  } else if (usage === "mobile") {
    features.weight_kg = 1.5;
    scalers.weight_kg = 3;
  }

  return {
    features, budget, country, brands, unknownIds,
    excludeInstructionSets, excludeBrands, requireDiscreteGpu, preferDiscreteGpu,
    minRamGb, minStorageGb, minCpuCinebench, scalers, reanchorSkip, allowRefurbished,
    requireTouchscreen,
  };
}
